import { isEmpty } from 'lodash';
import React, { FC } from 'react';
import { Formik, FormikHelpers } from 'formik';

import { userAuthService } from '@project/services';
import { useOtpResend } from '@project/hooks';

import { Button, FormTextField } from '../../atoms';
import { ConfirmationOtpValidation } from '../../../validators';
import { ErrorObject, BaseFormProps, ConfirmAccountOtp, ResendAccountOtp } from '../../../types';

export const ConfirmationOtpForm: FC<BaseFormProps<ConfirmAccountOtp>> = ({
  initialValues,
  submitForm,
  onSuccess,
}) => {
  const { isResending, resendMessage, countdown, handleResendOtp } = useOtpResend({
    resendFunction: userAuthService.resendAccountOtp,
    createResendData: (phoneNumber: string): ResendAccountOtp => ({
      phoneNumber,
      userConfirmationMethod: 2,
    }),
  });

  const _handleFormSubmitError = (
    error: ErrorObject,
    actions: FormikHelpers<ConfirmAccountOtp>,
  ) => {
    actions.setSubmitting(false);
    const apiErrors = error.errors;
    if (!isEmpty(apiErrors)) {
      if (apiErrors.accountVerificationIncomplete) {
        actions.setFieldError('email', apiErrors.email);
        actions.setFieldError('otp', apiErrors.Otp);
      } else actions.setFieldError('phoneNumber', apiErrors.phoneNumber);
    } else {
      actions.setFieldError('phoneNumber', error.message);
    }
  };

  const _handleSubmission = (
    formData: ConfirmAccountOtp,
    actions: FormikHelpers<ConfirmAccountOtp>,
  ) => {
    submitForm(formData)
      .then(() => {
        actions.setSubmitting(false);
        onSuccess();
      })
      .catch((error: ErrorObject) => {
        _handleFormSubmitError(error, actions);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ConfirmationOtpValidation}
      validateOnBlur
      validateOnChange={false}
      onSubmit={_handleSubmission}
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleReset,
      }) => (
        <form onReset={handleReset} onSubmit={handleSubmit} className="auth-form-body">
          <p className="auth-heading">Confirm Account</p>
          <p className="auth-sub-heading">Enter the OTP to confirm account.</p>
          <div className="w-4/5">
            <FormTextField
              variableName="otp"
              placeholder="OTP Number"
              className="w-full self-center"
              values={values}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched}
              errors={errors}
              icon={<img src="assets/icons/lock-icon.png" alt="mail" />}
            />
            <div className="w-full flex justify-start pt-2">
              {countdown > 0 ? (
                <span className="text-xxs text-gray-500">Resend OTP in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleResendOtp(values.phoneNumber)}
                  disabled={isResending || isSubmitting}
                  className={`text-xxs underline ${
                    isResending || isSubmitting
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 hover:text-blue-800 cursor-pointer'
                  }`}
                >
                  {isResending ? 'Resending...' : 'Resend OTP'}
                </button>
              )}
            </div>
          </div>

          {resendMessage && (
            <p
              className={`text-sm ${
                resendMessage.includes('Failed') ? 'text-red-500' : 'text-green-500'
              }`}
            >
              {resendMessage}
            </p>
          )}
          <Button
            isLoading={isSubmitting}
            onClick={handleSubmit}
            className="auth-form-submit-button"
          >
            Confirm Account
          </Button>
        </form>
      )}
    </Formik>
  );
};
